import { ANALYTICS_SEPARATOR } from '../events';
import { mapProduct } from './mapProduct';

/**
 * @typedef {Object} breadcrumb
 * @property {String} clickable
 * @property {String} displayName
 *
 * Convert field names within breadcrumb objects to those used by Analytics
 * @param {Object[]} breadcrumbs
 * @returns {Object[]}
 */
export const convertBreadcrumbs = (breadcrumbs) =>
  breadcrumbs
    ? breadcrumbs.map((breadcrumb) => {
        const { clickable, displayName } = breadcrumb;
        return {
          value: displayName ? displayName.toLowerCase() : '',
          clickable,
        };
      })
    : [];

/**
 * Convert delivery type codes into the abbreviated format used by Analytics
 * @param {String} deliveryTypeCode
 * @returns {String}
 */
const getDeliveryType = (deliveryTypeCode) => {
  switch (deliveryTypeCode) {
    case 'UK_DELIVERY':
      return 'uk';
    case 'CLICK_AND_COLLECT':
      return 'cc';
    case 'COLLECT_PLUS':
      return 'cp';
    case 'INTERNATIONAL':
      return 'int';
    default:
      return deliveryTypeCode.toLowerCase();
  }
};

/**
 * @typedef { deliveryTypes } deliveryInformation
 * @property { deliveryType[] } deliveryTypes
 *
 * @typedef { { deliveryTypeCode: string } } deliveryType
 *
 * Create an {ANALYTICS_SEPARATOR}-delimited list of delivery options (in Analytics format) that apply to this sku
 * @param {Object} deliveryInformation
 * @returns {String}
 */
export const extractDeliveryOptions = (deliveryInformation) =>
  deliveryInformation?.deliveryTypes
    ? deliveryInformation.deliveryTypes
        .map((deliveryType) => getDeliveryType(deliveryType.deliveryTypeCode))
        .join(ANALYTICS_SEPARATOR)
    : '';

export const getCompareProductDetails = (products, open, sponsoredProducts) =>
  products && open
    ? products.map((product) => {
        const { productId, skuId, price = '' } = product;
        const compareProduct = {
          sku: skuId,
          productId,
          itemPrice: price ? price.replace('£', '') : '',
        };

        const sponsoredProduct = sponsoredProducts.find(
          (product) => product.productId === compareProduct.productId
        );

        if (sponsoredProduct) compareProduct.citrusAdId = sponsoredProduct.adId;

        return compareProduct;
      })
    : [];

/**
 * @typedef {Object} sku
 * @property {Integer} stockLevel
 * @property {String} id
 *
 * Create an {ANALYTICS_SEPARATOR}-delimited list of skuIds that are out of stock at the moment
 * @param {Object[]} skus
 * @returns {String}
 *
 * e.g. [{id: 1111, stockLevel: 1}, {id: 2222, stockLevel: 0}, {id: 3333, stockLevel: 3}, {id: 4444, stockLevel: 0}]
 *      becomes "2222|4444"
 */
export const getOutOfStockSkus = (skus) =>
  skus
    ? skus
        .filter((sku) => sku.stockLevel === 0)
        .map((sku) => sku.id)
        .join(ANALYTICS_SEPARATOR)
    : '';

export const extractAlternativeSearchSuggestions = (search) =>
  search && search.alternativeSearchSuggestions
    ? search.alternativeSearchSuggestions
        .map(({ searchTerm }) => searchTerm)
        .join(ANALYTICS_SEPARATOR)
    : '';

export const extractProducts = ({
  productData = {},
  search = {},
  chunk,
  chunkSize,
  isChunkSizeExperiment,
  pageSize,
}) => {
  const prods = productData.products;
  const { personalisedPlp } = search;

  if (prods) {
    //TODO: This check worries me. products should not be undefined.
    const slicedProducts = prods.slice(0, chunkSize);

    const productIds = slicedProducts.map((product) => product.id);

    const first6Products = prods?.slice(0, 6);

    const first6OutOfStock = first6Products
      .map((product) => (product.outOfStock ? 'outofstock' : 'instock'))
      .join(ANALYTICS_SEPARATOR);

    const first6ProductIDs = first6Products
      .map((product) => product.id)
      .join(ANALYTICS_SEPARATOR);

    const first6Reviews = first6Products
      .map((product) => product.rating?.reviews)
      .join(ANALYTICS_SEPARATOR);

    const first6StarRatings = first6Products
      .map((product) => product.rating?.averageRating)
      .join(ANALYTICS_SEPARATOR);

    const first6Promotions = first6Products
      .map((product) =>
        product.promoMessages?.length > 0 ? product.promoMessages[0] : ''
      )
      .join(ANALYTICS_SEPARATOR);

    const results = {
      displayNum: slicedProducts.length,
      pagination: productData.pagesAvailable,
      totalNumberOfResults: productData.totalNumberOfResults,
      pageNum: productData.pageNumber,
      first6OutOfStock,
      first6ProductIDs,
      first6Promotions,
      first6Reviews,
      first6StarRatings,
      productIds,
    };

    return {
      products: {
        chunkDetails: {
          chunkOffset: 0,
          products:
            chunk == 8 ||
            (isChunkSizeExperiment && chunkSize === pageSize) ||
            (prods.length > productData.totalNumberOfResults &&
              productData.totalNumberOfResults % chunkSize === 0)
              ? prods.map(mapProduct)
              : prods.slice(0, Number(chunk, 10) * chunkSize).map(mapProduct),
          personalisedPlp,
        },
      },
      results,
    };
  }

  return {
    //TODO: This should never run in production: ProductContext should provide an empty array ( ([]) !== undefined )
    products: {},
    search: {},
  };
};

/**
 * @param {Object[]} breadcrumbs
 * @param {String} productId
 * @returns {String}
 */
export const getPageName = (breadcrumbs, productId) =>
  ['jl']
    .concat(
      breadcrumbs ? breadcrumbs.map((breadcrumb) => breadcrumb.displayName) : []
    )
    .concat(productId || [])
    .join(':')
    .toLowerCase();

const extractSponsoredProducts = (products) => {
  if (products) {
    //TODO: Remove this once ProductContext defaults are discussed
    return products
      .filter((product) => 'adId' in product)
      .map((product) => ({
        adId: product.adId,
        productId: product.id,
        type: 'sponsoredProduct',
      }));
  }
  return [];
};

export const getAnalyticsData = ({
  chunk,
  chunkSize,
  compare,
  facets,
  isChunkSizeExperiment,
  isStandaloneCompareFlagOn,
  page,
  pageSize,
  productData,
  search,
  vertex,
}) => {
  const extractedProductInformation = extractProducts({
    productData,
    search,
    chunk,
    chunkSize,
    isChunkSizeExperiment,
    pageSize,
  });

  const sponsoredProducts = extractSponsoredProducts(productData.products);

  const alternativeSearchSuggestions =
    extractAlternativeSearchSuggestions(search);

  const { applied } = facets;
  const { filterCombination, filterCombinationNvals } = applied;

  const data = {
    filter: filterCombination,
    filterNvals: filterCombinationNvals,
    products: extractedProductInformation.products,
    search: {
      ...extractedProductInformation.results,
    },
    sponsoredProducts,
    vertex,
  };

  if (!isStandaloneCompareFlagOn) {
    data.compare = {
      product: getCompareProductDetails(
        compare.data,
        compare.isOpen,
        sponsoredProducts
      ),
    };
  }

  const { type } = page;

  switch (type) {
    case 'search': {
      data.search = {
        ...data.search,
        ...extractedProductInformation.results,
        searchTermDidYouMean: alternativeSearchSuggestions,
        searchTermRecommended: search?.correctedSearchTerm || '',
        redirectType: search?.redirectType || 'no redirect',
      };
      break;
    }
    case 'brand':
    case 'browse': {
      data.products = {
        ...data.products,
        ...extractedProductInformation.results,
      };
      break;
    }
    default: {
      break;
    }
  }

  return { data };
};
